import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ListedGroup } from "../../../api/access/accessModel";
import AppButton from "../../../ui/buttons/AppButton";
import Field from "../../../ui/forms/Field";
import ModalShell from "../../../ui/overlays/ModalShell";
import { FORM_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import { groupChange } from "../changes/accessChanges";
import type { AccessChanges } from "../changes/useAccessChanges";

interface RenameGroupDialogProps {
  group: ListedGroup;
  /** Its name as the screen shows it. */
  name: string;
  changes: AccessChanges;
  /** The installation groups it may sit inside, with their names; omitted for a project's group. */
  parents?: readonly { readonly groupId: string; readonly name: string }[];
  onClose: () => void;
}

/** A group's new name, and for an installation group the group it sits inside (GS-1, GR-2). */
export default function RenameGroupDialog({ group, name, changes, parents, onClose }: RenameGroupDialogProps) {
  const { t } = useTranslation();
  const formId = useId();
  const [newName, setNewName] = useState(name);
  const [parentId, setParentId] = useState(group.parentId ?? "");
  const trimmed = newName.trim();
  const unchanged = trimmed === name && parentId === (group.parentId ?? "");
  return (
    <ModalShell
      title={t("access.renameTitle", { group: name })}
      onClose={onClose}
      size="md"
      zIndexClassName="z-[60]"
      footer={
        <div className="flex justify-end gap-2">
          <AppButton variant="secondary" size="sm" onClick={onClose}>{t("common.cancel")}</AppButton>
          <AppButton type="submit" form={formId} variant="primary" size="sm" disabled={trimmed === "" || unchanged}>{t("access.save")}</AppButton>
        </div>
      }
    >
      <form id={formId} className="space-y-3 text-sm"
        onSubmit={(event) => {
          event.preventDefault();
          changes.run(groupChange(t, { groupId: group.groupId, name, parentId: group.parentId }, trimmed, parentId === "" ? null : parentId));
          onClose();
        }}>
        <Field label={t("access.name")}>
          {(control) => <input {...control} className={FORM_CONTROL_CLASS} value={newName} onChange={(event) => { setNewName(event.target.value); }} />}
        </Field>
        {parents === undefined ? null : (
          <Field label={t("access.inside")}>
            {(control) => (
              <select {...control} className={FORM_CONTROL_CLASS} value={parentId} onChange={(event) => { setParentId(event.target.value); }}>
                <option value="">{t("access.atTop")}</option>
                {parents.map((parent) => <option key={parent.groupId} value={parent.groupId}>{parent.name}</option>)}
              </select>
            )}
          </Field>
        )}
      </form>
    </ModalShell>
  );
}
